import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RestTimer = ({ 
  duration = 90,
  onComplete,
  onSkip,
  isActive = false
}) => {
  const [timeRemaining, setTimeRemaining] = useState(duration);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!isActive || isPaused) return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          onComplete?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, isPaused, onComplete]);

  useEffect(() => {
    setTimeRemaining(duration);
  }, [duration]);

  const progress = ((duration - timeRemaining) / duration) * 100;
  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;

  const motivationalMessages = [
    "Great work! Take a breather.",
    "You\'re crushing it! Rest up.",
    "Excellent form! Recover strong.",
    "Keep pushing! You\'ve got this.",
    "Amazing effort! Recharge now."
  ];

  const currentMessage = motivationalMessages?.[Math.floor(Math.random() * motivationalMessages?.length)];

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-[110] bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl p-6 md:p-8">
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
            Rest Time
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            {currentMessage}
          </p>
        </div>

        <div className="relative w-48 h-48 md:w-56 md:h-56 mx-auto mb-6">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              stroke="var(--color-muted)"
              strokeWidth="12"
              fill="none"
            />
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              stroke="var(--color-accent)"
              strokeWidth="12"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 45}`}
              strokeDashoffset={`${2 * Math.PI * 45 * (1 - progress / 100)}`}
              strokeLinecap="round"
              className="transition-smooth"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-5xl md:text-6xl font-bold data-text text-foreground">
              {minutes}:{seconds?.toString()?.padStart(2, '0')}
            </div>
            <div className="text-sm caption text-muted-foreground mt-2">
              seconds remaining
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={() => setIsPaused(!isPaused)}
            className="flex-1"
            iconName={isPaused ? "Play" : "Pause"}
            iconPosition="left"
          >
            {isPaused ? 'Resume' : 'Pause'}
          </Button>
          <Button
            variant="default"
            onClick={onSkip}
            className="flex-1"
            iconName="SkipForward"
            iconPosition="left"
          >
            Skip Rest
          </Button>
        </div>

        <div className="mt-4 flex items-center justify-center gap-4">
          <button
            onClick={() => setTimeRemaining(prev => Math.min(duration, prev + 15))}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-smooth"
            aria-label="Add 15 seconds"
          >
            <Icon name="Plus" size={16} />
            <span>15s</span>
          </button>
          <button
            onClick={() => setTimeRemaining(prev => Math.max(0, prev - 15))}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-smooth"
            aria-label="Subtract 15 seconds"
          >
            <Icon name="Minus" size={16} />
            <span>15s</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestTimer;